<template>
  <v-navigation-drawer :model-value="drawer" :rail="rail" :width="280" :rail-width="68" :temporary="false"
    :scrim="false" elevation="0" color="surface" class="routebus-sidebar"
    @update:model-value="$emit('update:drawer', $event)">
    <div class="sidebar-layout">
      <div class="sidebar-brand">
        <router-link to="/" class="brand-link">
          <img :src="$isDark ? '/images/logo_main1.png' : '/images/logo_main.png'" alt="RouteBus" class="brand-logo" />
        </router-link>
      </div>

      <div class="sidebar-navigation">
        <v-list v-model:opened="openedGroups" nav density="compact" class="sidebar-menu" bg-color="transparent">
          <template v-for="item in menuItems" :key="item.label">
            <v-list-group v-if="item.children?.length" :value="item.label" class="sidebar-group">
              <template #activator="{ props }">
                <v-list-item v-bind="props" :prepend-icon="item.icon" :title="item.label" :active="false" :class="{
                  'sidebar-parent-active': isGroupActive(item)
                }" class="sidebar-item" rounded="lg" />
              </template>

              <v-list-item v-for="child in item.children" :key="child.route || child.label" :title="child.label"
                :active="false" :class="{
                  'sidebar-active-item': isActive(child.route)
                }" class="sidebar-child" rounded="lg" @click="navigate(child.route)">
                <template #prepend>
                  <span class="submenu-dot" :class="{
                    'submenu-dot-active': isActive(child.route)
                  }" />
                </template>
              </v-list-item>
            </v-list-group>

            <v-list-item v-else :prepend-icon="item.icon" :title="item.label" :active="false" :class="{
              'sidebar-active-item': isActive(item.route)
            }" class="sidebar-item" rounded="lg" @click="navigate(item.route)" />
          </template>
        </v-list>
      </div>

      <div v-if="!rail" class="sidebar-footer">
        <NeedHelp />
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import { MENUS, USER_ROLES } from "@/shared/resource/constants";
import NeedHelp from "./NeedHelp.vue";

export default {
  name: "SidebarUI",

  components: {
    NeedHelp,
  },

  emits: ["update:drawer"],

  props: {
    rail: {
      type: Boolean,
      default: false,
    },

    drawer: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      expandedGroups: [],
    };
  },

  computed: {
    menuItems() {
      if (!this.$auth) return [];
      console.log("User Role in Sidebar:", this.$auth); // Debugging line

      return this.$auth?.role === USER_ROLES.SUPERADMIN
        ? MENUS.superAdmin : this.$auth?.role === USER_ROLES.ADMIN
          ? MENUS.admin
          : MENUS.operator;
    },

    activeGroup() {
      return this.menuItems.find((item) =>
        this.isGroupActive(item)
      )?.label;
    },

    openedGroups: {
      get() {
        return this.activeGroup
          ? [...new Set([...this.expandedGroups, this.activeGroup])]
          : this.expandedGroups;
      },

      set(value) {
        this.expandedGroups = value;
      },
    },
  },

  methods: {
    navigate(route) {
      if (route && this.$route.path !== route) {
        this.$router.push(route);
      }
    },

    isActive(route) {
      return !!route && this.$route.path === route;
    },

    isGroupActive(item) {
      return !!item.children?.some((child) =>
        this.isActive(child.route)
      );
    },
  },
};
</script>

<style scoped>
.routebus-sidebar {
  --sidebar-bg: rgb(var(--v-theme-rb-sidebar-bg));
  --sidebar-text: rgb(var(--v-theme-rb-sidebar-text));
  --sidebar-muted: rgb(var(--v-theme-rb-sidebar-muted));
  --sidebar-hover: rgb(var(--v-theme-rb-sidebar-hover));
  --sidebar-active: rgb(var(--v-theme-rb-sidebar-active));
  --sidebar-active-text: rgb(var(--v-theme-rb-sidebar-active-text));
  --sidebar-border: rgb(var(--v-theme-rb-sidebar-line));

  background: var(--sidebar-bg) !important;
  /* border-right: 1px solid var(--sidebar-border) !important; */
  box-shadow: 3px 0 16px rgba(15, 35, 70, 0.05) !important;
}

.routebus-sidebar :deep(.v-navigation-drawer__content) {
  height: 100%;
  overflow: hidden;
  background: var(--sidebar-bg);
}

.sidebar-layout {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  padding: 0;
  background: var(--sidebar-bg);
}

.sidebar-brand {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 68px;
  padding: 8px;
  border-bottom: 1px solid var(--sidebar-border);
}

.brand-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  text-decoration: none;
}

.brand-logo {
  display: block;
  width: 160px;
  max-width: 100%;
  height: 40px;
  object-fit: contain;
}

.sidebar-navigation {
  flex: 1;
  min-height: 0;
  padding-top: 12px;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--sidebar-border) transparent;
}

.sidebar-navigation::-webkit-scrollbar {
  width: 4px;
}

.sidebar-navigation::-webkit-scrollbar-thumb {
  background: var(--sidebar-border);
  border-radius: 8px;
}

.sidebar-menu {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0 !important;
  background: transparent !important;
}

.sidebar-menu :deep(.v-list-item__overlay),
.sidebar-menu :deep(.v-list-item__underlay) {
  background: transparent !important;
  opacity: 0 !important;
}

.sidebar-menu :deep(.v-list-item--active),
.sidebar-menu :deep(.v-list-item--link),
.sidebar-menu :deep(.v-list-item--variant-text) {
  --v-activated-opacity: 0;
  --v-hover-opacity: 0;
  --v-focus-opacity: 0;
}

.sidebar-menu :deep(.v-list-item) {
  min-height: 40px;
  margin: 0;
  padding: 0 12px;
  border-radius: 8px;
  background: transparent !important;
  color: var(--sidebar-text) !important;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.sidebar-menu :deep(.v-list-item__prepend) {
  align-self: center;
  flex-shrink: 0;
}

.sidebar-menu :deep(.sidebar-item .v-list-item__prepend > .v-icon) {
  width: 20px;
  margin-inline-end: 4px;
  font-size: 20px;
  opacity: 1;
  color: var(--sidebar-muted) !important;
}

.sidebar-menu :deep(.sidebar-item .v-list-item__spacer) {
  width: 10px;
}

.sidebar-menu :deep(.v-list-item__content) {
  min-width: 0;
}

.sidebar-menu :deep(.v-list-item-title) {
  color: inherit !important;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.5;
  letter-spacing: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-menu :deep(.sidebar-item:not(.sidebar-active-item):not(.sidebar-parent-active):hover) {
  background: var(--sidebar-hover) !important;
}

.sidebar-menu :deep(.sidebar-parent-active) {
  background: var(--sidebar-active) !important;
  color: var(--sidebar-active-text) !important;
}

.sidebar-menu :deep(.sidebar-parent-active .v-list-item-title) {
  color: var(--sidebar-active-text) !important;
  font-weight: 700 !important;
}

.sidebar-menu :deep(.sidebar-parent-active .v-icon) {
  color: rgb(var(--v-theme-primary)) !important;
}

.sidebar-menu :deep(.sidebar-item.sidebar-active-item) {
  background: var(--sidebar-active) !important;
  color: var(--sidebar-active-text) !important;
}

.sidebar-menu :deep(.sidebar-item.sidebar-active-item .v-list-item-title) {
  color: var(--sidebar-active-text) !important;
  font-weight: 700 !important;
}

.sidebar-menu :deep(.sidebar-item.sidebar-active-item .v-icon) {
  color: rgb(var(--v-theme-primary)) !important;
}

.sidebar-menu :deep(.v-list-group__items) {
  position: relative;
  margin: 4px 0 4px 22px;
  padding-left: 10px;
  border-left: 1px solid var(--sidebar-border);
}

.sidebar-menu :deep(.sidebar-child) {
  position: relative;
  min-height: 36px;
  margin: 0 0 2px;
  padding-inline-start: 12px !important;
  border-radius: 7px;
  background: transparent !important;
  color: var(--sidebar-muted) !important;
}

.sidebar-menu :deep(.sidebar-child .v-list-item__prepend) {
  position: absolute;
  left: -14px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  margin: 0 !important;
}

.sidebar-menu :deep(.sidebar-child .v-list-item__spacer) {
  display: none !important;
}

.submenu-dot {
  display: block;
  width: 5px;
  height: 5px;
  flex-shrink: 0;
  border-radius: 50%;
  background: var(--sidebar-border);
  box-shadow: 0 0 0 3px var(--sidebar-bg);
  transition: background-color 0.2s ease;
}

.submenu-dot-active {
  width: 6px;
  height: 6px;
  background: rgb(var(--v-theme-primary));
}

.sidebar-menu :deep(.sidebar-child .v-list-item-title) {
  color: var(--sidebar-muted) !important;
  font-size: 12.5px;
  font-weight: 700 !important;
  letter-spacing: 0;
}

.sidebar-menu :deep(.sidebar-child:not(.sidebar-active-item):hover) {
  background: var(--sidebar-hover) !important;
}

.sidebar-menu :deep(.sidebar-child.sidebar-active-item),
.sidebar-menu :deep(.sidebar-child.sidebar-active-item:hover) {
  background: var(--sidebar-active) !important;
  color: var(--sidebar-active-text) !important;
}

.sidebar-menu :deep(.sidebar-child.sidebar-active-item .v-list-item-title) {
  color: var(--sidebar-active-text) !important;
  font-weight: 700 !important;
}

.sidebar-menu :deep(.sidebar-child.sidebar-active-item .submenu-dot) {
  background: rgb(var(--v-theme-primary)) !important;
}

.sidebar-footer {
  flex-shrink: 0;
  padding: 12px 0 16px;
}

.routebus-sidebar.v-navigation-drawer--rail .sidebar-layout {
  padding: 0 6px;
}

.routebus-sidebar.v-navigation-drawer--rail .sidebar-brand {
  padding: 6px 0;
}

.routebus-sidebar.v-navigation-drawer--rail .brand-logo {
  width: 42px;
  height: 42px;
}

.routebus-sidebar.v-navigation-drawer--rail .sidebar-menu :deep(.v-list-item) {
  padding-inline: 12px;
}

@media (max-width: 600px) {
  .sidebar-brand {
    flex-basis: 60px;
  }
}

@media (prefers-reduced-motion: reduce) {

  .sidebar-menu :deep(.v-list-item),
  .submenu-dot {
    transition: none;
  }
}
</style>